import { Auth } from 'aws-amplify';
import cdkConfig from '../config/cdk-exports';

interface AnalyticsData {
  dailyMetrics: Array<{
    id: string;
    date: string;
    totalVisits: number;
    uniqueUsers: number;
    transcriptionJobs: number;
    successfulJobs: number;
    failedJobs: number;
    totalMinutesTranscribed: number;
    totalCost: number;
    translationJobs: number;
    createdAt: string;
    updatedAt: string;
  }>;
  userMetrics: {
    id: string;
    totalUsers: number;
    activeUsers30d: number;
    newUsersToday: number;
    retentionRate: number;
    avgSessionDuration: number;
    lastUpdated: string;
  };
  languageStats: Array<{
    id: string;
    language: string;
    transcriptionCount: number;
    translationCount: number;
    successRate: number;
    lastUpdated: string;
  }>;
  costAnalytics: {
    totalCost: number;
    costPerMinute: number;
    costSavings: number;
    period: string;
  };
  completeSummary: {
    totalJobs: number;
    successfulJobs: number;
    failedJobs: number;
    totalMinutesTranscribed: number;
    totalCost: number;
    lastUpdated: string;
    note: string;
  };
}

const GET_ANALYTICS_DASHBOARD = `
  query GetAnalyticsDashboard($startDate: String!, $endDate: String!) {
    getAnalyticsDashboard(startDate: $startDate, endDate: $endDate) {
      dailyMetrics {
        id
        date
        totalVisits
        uniqueUsers
        transcriptionJobs
        successfulJobs
        failedJobs
        totalMinutesTranscribed
        totalCost
        translationJobs
        createdAt
        updatedAt
      }
      userMetrics {
        id
        totalUsers
        activeUsers30d
        newUsersToday
        retentionRate
        avgSessionDuration
        lastUpdated
      }
      languageStats {
        id
        language
        transcriptionCount
        translationCount
        successRate
        lastUpdated
      }
      costAnalytics {
        totalCost
        costPerMinute
        costSavings
        period
      }
      completeSummary {
        totalJobs
        successfulJobs
        failedJobs
        totalMinutesTranscribed
        totalCost
        lastUpdated
        note
      }
    }
  }
`;

export class AnalyticsCDKService {
  private apiUrl: string;
  private region: string;

  constructor() {
    this.apiUrl = cdkConfig.analytics.apiUrl;
    this.region = cdkConfig.analytics.region;
  }

  async getAnalyticsDashboard(startDate?: string, endDate?: string): Promise<AnalyticsData> {
    // Default to last 30 days if no dates provided
    const defaultEnd = new Date().toISOString().split('T')[0];
    const defaultStart = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];

    const variables = {
      startDate: startDate || defaultStart,
      endDate: endDate || defaultEnd
    };

    try {
      // Get current user session for authentication
      const session = await Auth.currentSession();
      const token = session.getIdToken().getJwtToken();

      const response = await fetch(this.apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': token
        },
        body: JSON.stringify({
          query: GET_ANALYTICS_DASHBOARD,
          variables
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      
      if (result.errors) {
        throw new Error(result.errors[0].message);
      }

      return result.data.getAnalyticsDashboard;
    } catch (error) {
      console.error('Error fetching analytics from CDK:', error);
      throw error;
    }
  }
}

export const analyticsCDKService = new AnalyticsCDKService();
